import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface PriceHistoryEntry {
  date: number;
  dateFormatted: string;
  unitPrice: number;
  unitPriceFormatted: string;
  totalPrice: number;
  totalPriceFormatted: string;
  quantity: number;
  receiptId: string;
}

interface ProductHistory {
  productName: string;
  productNameOriginal: string;
  history: PriceHistoryEntry[];
  minPrice: string;
  maxPrice: string;
  avgPrice: string;
  lastPrice: string;
  priceChangeCount: number;
}

interface PriceHistoryResponse {
  products: ProductHistory[];
  totalProducts: number;
}

export default function PriceHistoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const productParam = searchParams.get('product');
  const [data, setData] = useState<PriceHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductHistory | null>(null);
  
  // Initialize state from URL params or use defaults
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState<'frequency' | 'name' | 'price' | 'recent'>(
    (searchParams.get('sortBy') as 'frequency' | 'name' | 'price' | 'recent') || 'frequency'
  );
  const navigate = useNavigate();

  // Function to update URL when state changes
  const updateUrlState = (updates: {
    search?: string;
    sortBy?: 'frequency' | 'name' | 'price' | 'recent';
    product?: string;
  }) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (updates.search !== undefined) {
        if (updates.search) {
          newParams.set('search', updates.search);
        } else {
          newParams.delete('search');
        }
      }
      if (updates.sortBy !== undefined) {
        if (updates.sortBy !== 'frequency') {
          newParams.set('sortBy', updates.sortBy);
        } else {
          newParams.delete('sortBy');
        }
      }
      if (updates.product !== undefined) {
        if (updates.product) {
          newParams.set('product', updates.product);
        } else {
          newParams.delete('product');
        }
      }
      return newParams;
    });
  };

  // Wrapper functions that update both state and URL
  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    updateUrlState({ search: term });
  };

  const handleSortChange = (sort: 'frequency' | 'name' | 'price' | 'recent') => {
    setSortBy(sort);
    updateUrlState({ sortBy: sort });
  };

  const handleProductSelect = (product: ProductHistory) => {
    setSelectedProduct(product);
    updateUrlState({ product: product.productName });
  };

  // Sync URL params to state when they change (back/forward navigation)
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    const sortFromUrl = (searchParams.get('sortBy') as 'frequency' | 'name' | 'price' | 'recent') || 'frequency';
    const productFromUrl = searchParams.get('product');
    
    setSearchTerm(searchFromUrl);
    setSortBy(sortFromUrl);

    // If productParam changes from back/forward navigation, update selectedProduct
    if (productFromUrl && productFromUrl !== productParam) {
      const targetProduct = data?.products.find(
        (p: ProductHistory) => p.productName.toLowerCase() === productFromUrl.toLowerCase()
      );
      setSelectedProduct(targetProduct || data?.products[0] || null);
    } else if (productParam && data?.products && data.products.length > 0) {
      // If productParam was set initially and data is loaded, find and select that product
      const targetProduct = data.products.find(
        (p: ProductHistory) => p.productName.toLowerCase() === productParam.toLowerCase()
      );
      setSelectedProduct(targetProduct || data.products[0]);
    }
  }, [searchParams, productParam, data]);

  useEffect(() => {
    const fetchPriceHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiClient.get(`/api/price-history`);
        setData(response.data);
        
        // If a product parameter was passed, find and select that product
        if (productParam && response.data.products && response.data.products.length > 0) {
          const targetProduct = response.data.products.find(
            (p: ProductHistory) => p.productName.toLowerCase() === productParam.toLowerCase()
          );
          setSelectedProduct(targetProduct || response.data.products[0]);
        } else if (response.data.products && response.data.products.length > 0) {
          setSelectedProduct(response.data.products[0]);
        }
      } catch (err) {
        console.error('Failed to fetch price history:', err);
        setError('Failed to load price history. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPriceHistory();
  }, [productParam]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading price history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
        <h2 className="text-lg font-bold mb-2">Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!data || data.totalProducts === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-yellow-700">
        <p>No price history available yet. Upload some receipts to see price trends.</p>
      </div>
    );
  }

  const filteredProducts = data.products.filter((p) =>
    p.productName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Prepare chart data with conversion from pence to pounds
  const chartData =
    selectedProduct?.history.map((entry) => ({
      date: entry.dateFormatted,
      timestamp: entry.date, // Unix timestamp in seconds
      price: parseFloat((entry.unitPrice / 100).toFixed(2)),
      quantity: entry.quantity,
    })) || [];

  // Sort products based on selected sort option
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'frequency':
        // Most frequently purchased (most purchase count)
        return b.history.length - a.history.length;
      case 'name':
        // Alphabetical order
        return a.productName.localeCompare(b.productName);
      case 'price':
        // By average price (highest first)
        return parseFloat(b.avgPrice.replace('£', '')) - parseFloat(a.avgPrice.replace('£', ''));
      case 'recent': {
        // Most recent purchases (latest date first)
        const aLatest = a.history[a.history.length - 1]?.date || 0;
        const bLatest = b.history[b.history.length - 1]?.date || 0;
        return bLatest - aLatest;
      }
      default:
        return 0;
    }
  });

  // Custom formatter for time axis
  const formatTimeAxisDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-GB', { 
      month: 'short', 
      day: 'numeric',
      year: '2-digit'
    });
  };

  return (
    <div className="page-shell">
      <div className="flex flex-col gap-3">
        <h1 className="text-3xl font-semibold text-ink-700">Price history</h1>
        <p className="text-sm text-ink-500 max-w-3xl">
          Explore how each staple has moved over time. Filter by frequency, recent activity, or price to surface the trends that matter.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:items-start">
        <section className="card max-h-[calc(100vh-300px)] flex flex-col overflow-hidden lg:col-span-1">
          <div className="space-y-4 border-b border-ink-100/70 p-6 flex-shrink-0">
            <h2 className="text-lg font-semibold text-ink-700">Products</h2>
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full rounded-xl border border-ink-100/80 bg-white px-4 py-2 text-sm text-ink-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value as 'frequency' | 'name' | 'price' | 'recent')}
              className="w-full rounded-xl border border-ink-100/80 bg-white px-4 py-2 text-sm text-ink-600 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="frequency">Sort by: most frequent</option>
              <option value="recent">Sort by: most recent</option>
              <option value="price">Sort by: highest price</option>
              <option value="name">Sort by: name (A-Z)</option>
            </select>
          </div>
          <div className="flex-1 divide-y divide-ink-100/80 overflow-y-auto">
            {sortedProducts.map((product) => {
              const isActive = selectedProduct?.productName === product.productName;
              return (
                <button
                  key={product.productName}
                  onClick={() => handleProductSelect(product)}
                  className={`w-full px-6 py-4 text-left text-sm transition ${
                    isActive
                      ? 'bg-primary-50 text-primary-600 ring-1 ring-primary-200'
                      : 'text-ink-600 hover:bg-ink-50/80'
                  }`}
                >
                  <p className="font-semibold text-ink-700 truncate">{product.productNameOriginal}</p>
                  <p className="text-xs text-ink-400 mt-1">{product.history.length} purchases</p>
                  <p className="text-xs text-ink-300 mt-1">Avg: {product.avgPrice} · Last: {product.lastPrice}</p>
                </button>
              );
            })}
          </div>
        </section>

        <section className="lg:col-span-2 space-y-8">
          {selectedProduct && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="card p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Min price</p>
                  <p className="mt-2 text-2xl font-semibold text-ink-700">{selectedProduct.minPrice}</p>
                </div>
                <div className="card p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Max price</p>
                  <p className="mt-2 text-2xl font-semibold text-ink-700">{selectedProduct.maxPrice}</p>
                </div>
                <div className="card p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Average price</p>
                  <p className="mt-2 text-2xl font-semibold text-ink-700">{selectedProduct.avgPrice}</p>
                </div>
                <div className="card p-5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Price changes</p>
                  <p className="mt-2 text-2xl font-semibold text-ink-700">{selectedProduct.priceChangeCount}</p>
                </div>
              </div>

              <div className="card space-y-5 p-6">
                <div>
                  <h3 className="text-lg font-semibold text-ink-700">
                    Price trend · {selectedProduct.productNameOriginal}
                  </h3>
                  <p className="text-sm text-ink-500 mt-1">Smoothed unit price across your purchase history.</p>
                </div>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={380}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="timestamp"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={formatTimeAxisDate}
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis label={{ value: 'Unit price (£)', angle: -90, position: 'insideLeft' }} />
                      <Tooltip
                        formatter={(value) => `£${(value as number).toFixed(2)}`}
                        labelFormatter={(label) => {
                          const ts = typeof label === 'number' ? label : Number(label);
                          return formatTimeAxisDate(Number.isFinite(ts) ? ts : 0);
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="price"
                        stroke="#2563eb"
                        strokeWidth={2}
                        name="Unit price (£)"
                        isAnimationActive={false}
                        dot={{ fill: '#2563eb', r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="rounded-xl border border-dashed border-ink-200/80 bg-white/60 p-8 text-center text-sm text-ink-500">
                    No price data available yet.
                  </div>
                )}
              </div>

              <div className="card overflow-hidden">
                <div className="border-b border-ink-100/70 p-6">
                  <h3 className="text-lg font-semibold text-ink-700">Purchase history</h3>
                </div>
                <div className="table-card border-transparent">
                  <table className="table-default">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th className="text-center">Qty</th>
                        <th className="text-right">Unit price</th>
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-ink-100/80">
                      {selectedProduct.history.map((entry, idx) => (
                        <tr key={idx}>
                          <td className="font-medium text-ink-700">
                            <button
                              onClick={() => navigate(`/orders/${entry.receiptId}`)}
                              className="text-left text-accent-600 underline decoration-dotted underline-offset-4 transition hover:text-accent-500"
                            >
                              {entry.dateFormatted}
                            </button>
                          </td>
                          <td className="text-center text-ink-500">{entry.quantity}</td>
                          <td className="text-right font-medium text-ink-600">{entry.unitPriceFormatted}</td>
                          <td className="text-right font-semibold text-ink-700">{entry.totalPriceFormatted}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
